import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { useRouter } from "../../../hooks/useRouter";
import { formatCurrency } from "../../../utils";
import FinanceContainer from "../containers/FinanceContainer";
import config from "../../../config";

interface AgeAnalysisRow {
  clientId: string;
  clientName: string;
  Current: number;
  "30 Days": number;
  "60 Days": number;
  "90 Days": number;
  "120+": number;
  total: number;
}

interface AgeAnalysisData {
  asAt: string;
  rows: AgeAnalysisRow[];
  totals: AgeAnalysisRow;
}

const CreditorsAgeAnalysis: React.FC = () => {
  const router = useRouter();
  const [data, setData] = useState<AgeAnalysisData | null>(null);
  const [loading, setLoading] = useState(true);
  const [asAtDate, setAsAtDate] = useState(
    new Date().toISOString().split("T")[0],
  );

  useEffect(() => {
    fetchAgeAnalysis();
  }, []);

  const fetchAgeAnalysis = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("vms_access_token");
      const response = await fetch(
        `${config.API_BASE_URL}/creditors/age-analysis?asAtDate=${asAtDate}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error("Error fetching age analysis:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchAgeAnalysis();
  };

  const handleExport = () => {
    if (!data) return;

    const headers = [
      "Creditor Name",
      "Current",
      "30 Days",
      "60 Days",
      "90 Days",
      "120+",
      "Total",
    ];
    const csvRows = [
      headers.join(","),
      ...data.rows.map((row) =>
        [
          `"${row.clientName}"`,
          row.Current,
          row["30 Days"],
          row["60 Days"],
          row["90 Days"],
          row["120+"],
          row.total,
        ].join(","),
      ),
      [
        "TOTAL",
        data.totals.Current,
        data.totals["30 Days"],
        data.totals["60 Days"],
        data.totals["90 Days"],
        data.totals["120+"],
        data.totals.total,
      ].join(","),
    ];

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `creditors-age-analysis-${asAtDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getCellColor = (value: number, band: string) => {
    if (value === 0) return "inherit";
    if (band === "120+") return "#ffebee";
    if (band === "90 Days") return "#fff3e0";
    if (band === "60 Days") return "#fffde7";
    if (band === "30 Days") return "#e3f2fd";
    return "#e8f5e9";
  };

  const summaryCards = data
    ? [
        {
          title: "Current (0-30 days)",
          value: data.totals.Current,
          color: "#4caf50",
        },
        {
          title: "30 Days (31-60)",
          value: data.totals["30 Days"],
          color: "#2196f3",
        },
        {
          title: "60 Days (61-90)",
          value: data.totals["60 Days"],
          color: "#ff9800",
        },
        {
          title: "90 Days (91-120)",
          value: data.totals["90 Days"],
          color: "#f44336",
        },
        { title: "120+ Days", value: data.totals["120+"], color: "#9c27b0" },
        { title: "Total Payable", value: data.totals.total, color: "#ff5722" },
      ]
    : [];

  return (
    <FinanceContainer>
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <IconButton onClick={() => router.push("/finance/creditors")}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">Creditors Age Analysis</Typography>
        </Box>

        {/* Controls */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 3,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <TextField
            type="date"
            label="As at Date"
            value={asAtDate}
            onChange={(e) => setAsAtDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={!data}
          >
            Export CSV
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : data ? (
          <>
            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {summaryCards.map((card, index) => (
                <Grid item xs={12} sm={6} md={2} key={index}>
                  <Card sx={{ backgroundColor: card.color, color: "white" }}>
                    <CardContent sx={{ textAlign: "center", py: 1 }}>
                      <Typography variant="caption">{card.title}</Typography>
                      <Typography variant="h6">
                        {formatCurrency(card.value)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* As At Date Display */}
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              Report as at: {data.asAt}
            </Typography>

            {/* Age Analysis Table */}
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#ff5722" }}>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Creditor Name
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: "white", fontWeight: "bold" }}
                    >
                      Current
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: "white", fontWeight: "bold" }}
                    >
                      30 Days
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: "white", fontWeight: "bold" }}
                    >
                      60 Days
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: "white", fontWeight: "bold" }}
                    >
                      90 Days
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: "white", fontWeight: "bold" }}
                    >
                      120+ Days
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: "white", fontWeight: "bold" }}
                    >
                      Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No outstanding amounts
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {data.rows.map((row) => (
                        <TableRow
                          key={row.clientId}
                          hover
                          onClick={() =>
                            router.push(`/finance/creditors/${row.clientId}`)
                          }
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>{row.clientName}</TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              backgroundColor: getCellColor(
                                row.Current,
                                "Current",
                              ),
                            }}
                          >
                            {formatCurrency(row.Current)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              backgroundColor: getCellColor(
                                row["30 Days"],
                                "30 Days",
                              ),
                            }}
                          >
                            {formatCurrency(row["30 Days"])}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              backgroundColor: getCellColor(
                                row["60 Days"],
                                "60 Days",
                              ),
                            }}
                          >
                            {formatCurrency(row["60 Days"])}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              backgroundColor: getCellColor(
                                row["90 Days"],
                                "90 Days",
                              ),
                            }}
                          >
                            {formatCurrency(row["90 Days"])}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              backgroundColor: getCellColor(
                                row["120+"],
                                "120+",
                              ),
                            }}
                          >
                            {formatCurrency(row["120+"])}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: "bold" }}>
                            {formatCurrency(row.total)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Totals Row */}
                      <TableRow sx={{ backgroundColor: "#fff3e0" }}>
                        <TableCell sx={{ fontWeight: "bold" }}>TOTAL</TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          {formatCurrency(data.totals.Current)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          {formatCurrency(data.totals["30 Days"])}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          {formatCurrency(data.totals["60 Days"])}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          {formatCurrency(data.totals["90 Days"])}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          {formatCurrency(data.totals["120+"])}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          {formatCurrency(data.totals.total)}
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <Typography color="error">
            Failed to load age analysis data
          </Typography>
        )}
      </Box>
    </FinanceContainer>
  );
};

export default CreditorsAgeAnalysis;
